<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PostSaleTopicForm from './PostSaleTopicForm.vue';
import PostSaleTopicSummary from './PostSaleTopicSummary.vue';

defineProps({
  canManage: { type: Boolean, default: false },
  catalog: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  selectedTopic: { type: Object, default: null },
  showForm: { type: Boolean, default: false },
  topics: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'closeForm',
  'create',
  'delete',
  'edit',
  'requestCorrection',
  'save',
]);
const { t } = useI18n();
const topicToDelete = ref(null);

const confirmDelete = () => {
  emit('delete', topicToDelete.value);
  topicToDelete.value = null;
};
</script>

<template>
  <LaisCard
    :title="t('POST_SALE.TOPICS.TITLE')"
    prefix-icon="i-lucide-list-checks"
    density="flush"
  >
    <div v-if="loading" class="grid gap-3 bg-lais-bg-soft p-5">
      <div
        v-for="index in 3"
        :key="index"
        class="h-16 animate-pulse rounded-lais-sm bg-white"
      />
    </div>
    <div
      v-else-if="!topics.length"
      class="bg-lais-bg-soft p-5 text-sm text-lais-text-muted"
    >
      {{ t('POST_SALE.TOPICS.EMPTY') }}
    </div>
    <div v-else class="divide-y divide-lais-border-soft bg-lais-bg-soft">
      <PostSaleTopicSummary
        v-for="topic in topics"
        :key="topic.id"
        :can-manage="canManage"
        :catalog="catalog"
        :topic="topic"
        @delete="topicToDelete = $event"
        @edit="emit('edit', $event)"
      />
    </div>
    <template #footer>
      <div class="flex justify-end">
        <LaisPrimaryButton
          v-if="canManage"
          variant="ghost"
          icon="i-lucide-plus"
          @click="emit('create')"
        >
          {{ t('POST_SALE.TOPICS.ACTIONS.ADD') }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisCard>

  <PostSaleTopicForm
    :catalog="catalog"
    :saving="saving"
    :selected-topic="selectedTopic"
    :show="showForm"
    @close="emit('closeForm')"
    @request-correction="emit('requestCorrection', $event)"
    @submit="emit('save', $event)"
  />

  <LaisConfirmModal
    :show="Boolean(topicToDelete)"
    :title="t('POST_SALE.TOPICS.DELETE.TITLE')"
    :description="t('POST_SALE.TOPICS.DELETE.DESCRIPTION')"
    :confirm-text="t('POST_SALE.TOPICS.ACTIONS.DELETE')"
    :is-loading="saving"
    icon="i-lucide-trash-2"
    @close="topicToDelete = null"
    @confirm="confirmDelete"
  />
</template>
